import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioAPI } from '../services/api';
import { dashboardAPI, DashboardBrokerSummary } from '../services/dashboardApi';
import { BROKER_LABELS, BrokerName } from '../services/brokerApi';
import { DashboardStats } from '../types';
import { Activity, TrendingUp, TrendingDown, Settings, AlertTriangle, FileText, Eye, Download } from 'lucide-react';
import { Container, Row, Col, Card, Table, Spinner, Badge, Button } from 'react-bootstrap';
import MainLayout from '../components/MainLayout';
import { niftyBuyingReportsAPI, NiftyBuyingReportInfo } from '../services/niftyBuyingApi';

// Subtitles here name each broker's REAL API/product (matching the doc citations already in
// each brokers/*Adapter.ts comment) — not decorative filler. Avatar letter + accent color give
// each broker card a quick visual anchor, client's explicit ask from a reference mockup.
const BROKER_META: Record<string, { avatar: string; subtitle: string; color: string }> = {
  DHAN: { avatar: 'D', subtitle: 'DhanHQ REST v2', color: '#0d6efd' },
  GROWW: { avatar: 'G', subtitle: 'Groww Trade API', color: '#16a34a' },
  ANGEL_ONE: { avatar: 'A', subtitle: 'Angel One SmartAPI', color: '#dc2626' },
  FYERS: { avatar: 'F', subtitle: 'FYERS API v3', color: '#f59e0b' },
  ALICE_BLUE: { avatar: 'AB', subtitle: 'Alice Blue ANT API', color: '#1f2937' },
  KOTAK_NEO: { avatar: 'K', subtitle: 'Kotak Neo TradeAPI', color: '#7c3aed' },
  UPSTOX: { avatar: 'U', subtitle: 'Upstox API v2/v3', color: '#6b7280' },
  ZERODHA: { avatar: 'Z', subtitle: 'Kite Connect v3', color: '#ea580c' },
};

// Previously duplicated MainLayout's entire sidebar/header/logout shell by hand instead of using
// <MainLayout> like every other page — any sidebar change (e.g. the Page Visibility feature) had
// to be made in two places or it silently drifted between them. Now just supplies page content.
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPnl, setCurrentPnl] = useState({ rs: 0, percent: 0 });
  const [brokerSummaries, setBrokerSummaries] = useState<DashboardBrokerSummary[]>([]);
  const [brokerSummariesLoading, setBrokerSummariesLoading] = useState(true);
  // Client-side fetch timestamp — a real "when did we last hear from this broker" marker, not
  // fabricated per-broker telemetry (no ping/latency data exists anywhere in this stack).
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  // Client's explicit ask: a Dashboard-facing daily PDF (NIFTY/SENSEX Buying's full order/fail
  // details) — only ever yesterday/today, per the client's own explicit scope.
  const [reports, setReports] = useState<NiftyBuyingReportInfo[]>([]);
  const [reportActionDate, setReportActionDate] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
    fetchBrokerPnlSummary();
    fetchReportsList();
    // Client's explicit ask: live-ish broker P&L on the Dashboard — polled on the same 30s
    // cadence as other "not ultra time-critical" live widgets elsewhere in this app, since this
    // hits 4-5 brokers' own positions/funds APIs per tick (not a cheap call to spam faster).
    const interval = setInterval(fetchBrokerPnlSummary, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBrokerPnlSummary = async () => {
    try {
      const res = await dashboardAPI.getPnlSummary();
      setBrokerSummaries(res.data.brokers);
      setLastSyncAt(new Date());
      // Confirmed live as a REAL bug: MainLayout's header P&L used to come from the same orphaned
      // `portfolio` table as the stale stat tiles below — always ₹0.00/0% regardless of real
      // activity. Now computed from this same real broker data: absolute P&L is a straight sum;
      // the % is P&L against total available capital across connected brokers with a real
      // balance figure (not every broker's own getXPnlSummary exposes one — see dashboardApi.ts),
      // since there's no other honest base to express a percentage against.
      // Client's explicit ask: FYERS should show on the Dashboard like every other broker —
      // ALICE_BLUE stays excluded (session has been broken/401 all session, never asked to fix).
      //
      // Commented out (not deleted) per client's explicit ask (2026-11-02): the header P/L Rs box
      // should show ONLY Zerodha's P&L, not every connected broker summed together. Restore this
      // block (and delete the Zerodha-only block below) if the client wants the combined total back.
      // const real = res.data.brokers.filter(b => b.broker !== 'ALICE_BLUE');
      // const rs = real.reduce((s, b) => s + (b.connected && b.realizedPnl !== null ? b.realizedPnl : 0) + (b.connected && b.unrealizedPnl !== null ? b.unrealizedPnl : 0), 0);
      // const totalBalance = real.reduce((s, b) => s + (b.connected && b.availableBalance !== null ? b.availableBalance : 0), 0);
      // setCurrentPnl({ rs, percent: totalBalance > 0 ? (rs / totalBalance) * 100 : 0 });

      // Client's explicit ask (2026-11-02): header P/L Rs box shows Zerodha's own P&L only — 0/0%
      // (not faked) whenever Zerodha isn't connected, same "never show a fake money value" rule
      // used everywhere else in this app.
      const zerodha = res.data.brokers.find(b => b.broker === 'ZERODHA');
      const zRs = (zerodha?.connected && zerodha.realizedPnl !== null ? zerodha.realizedPnl : 0) + (zerodha?.connected && zerodha.unrealizedPnl !== null ? zerodha.unrealizedPnl : 0);
      const zBalance = zerodha?.connected && zerodha.availableBalance !== null ? zerodha.availableBalance : 0;
      setCurrentPnl({ rs: zRs, percent: zBalance > 0 ? (zRs / zBalance) * 100 : 0 });
    } catch (error) {
      console.error('Failed to fetch broker P&L summary:', error);
    } finally {
      setBrokerSummariesLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const data = await portfolioAPI.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportsList = async () => {
    try {
      const res = await niftyBuyingReportsAPI.list();
      setReports(res.data.reports);
    } catch (error) {
      console.error('Failed to fetch reports list:', error);
    }
  };

  // Blob + object URL (not a bare `window.open(url)`) because the report API needs the same
  // Bearer-token header every other call here sends — a top-level new-tab navigation can't attach
  // one. "View" opens that object URL in a new tab (the browser's own PDF viewer); "Download"
  // triggers a save using the real filename via a throwaway <a download> click — same pattern,
  // different final step.
  const openReport = async (date: string, mode: 'view' | 'download') => {
    setReportActionDate(`${date}:${mode}`);
    try {
      const res = await niftyBuyingReportsAPI.fetchPdfBlob(date, mode);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (mode === 'view') {
        window.open(blobUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `nifty_buying_${date}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      // Give the new tab/download a moment to actually pick up the blob before revoking it.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error: any) {
      console.error(`Failed to ${mode} report for ${date}:`, error);
      // responseType:'blob' means even an error response body (e.g. the 409 "not ready yet" JSON)
      // arrives as a Blob, not parsed JSON — read it back out as text so the real backend message
      // (not a generic fallback) reaches the user.
      let message = `Could not ${mode} the report for ${date}. Please try again.`;
      const errorBlob = error?.response?.data;
      if (errorBlob instanceof Blob) {
        try {
          const text = await errorBlob.text();
          message = JSON.parse(text)?.error || message;
        } catch {
          // Fall back to the generic message above.
        }
      }
      alert(message);
    } finally {
      setReportActionDate(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-2" style={{ fontSize: '13px', fontWeight: '500' }}>Loading Dashboard...</div>
        </div>
      </div>
    );
  }

  const filteredBrokers = brokerSummaries.filter(b => b.broker !== 'ALICE_BLUE');
  const totalRealized = filteredBrokers.reduce((s, b) => s + (b.connected && b.realizedPnl !== null ? b.realizedPnl : 0), 0);
  const totalUnrealized = filteredBrokers.reduce((s, b) => s + (b.connected && b.unrealizedPnl !== null ? b.unrealizedPnl : 0), 0);

  return (
    <MainLayout pnlRs={currentPnl.rs} pnlPercent={currentPnl.percent}>
      <Container className="pb-3">
        {/* Stats + Broker summary — all in one row on desktop, wrap on smaller screens */}
        {(() => {
          // Confirmed live as a REAL bug (client report: Dashboard showed "0 positions, no
          // trades" while a real position was genuinely open): every value here now comes from
          // either the live broker P&L fetch (brokerSummaries, already real) or the rebuilt
          // backend aggregate (portfolioController.ts's getDashboardStats, now UNIONs every real
          // trading feature's own tables instead of an orphaned `portfolio` table). "Open Pos"
          // and the old separate "Open Pos (all brokers)" tile were the exact same real number
          // once both were fixed, so the duplicate was dropped.
          const openPositions = filteredBrokers.reduce((s, b) => s + b.positions.length, 0);
          const statCards: { icon?: any; label: string; value: string | number; color: string }[] = [
            { icon: Activity, label: 'Total Orders', value: stats?.totalOrdersPlaced || 0, color: '#007bff' },
            { icon: TrendingUp, label: 'Open Pos', value: openPositions, color: '#28a745' },
            { icon: TrendingDown, label: 'Total P&L', value: formatCurrency(totalRealized + totalUnrealized), color: (totalRealized + totalUnrealized) >= 0 ? '#28a745' : '#dc3545' },
            { icon: Settings, label: 'Active Con', value: stats?.configs.active_configs || 0, color: '#007bff' },
            { label: 'Total Realized P&L', value: formatCurrency(totalRealized), color: totalRealized >= 0 ? '#28a745' : '#dc3545' },
            { label: 'Total Unrealized P&L', value: formatCurrency(totalUnrealized), color: totalUnrealized >= 0 ? '#28a745' : '#dc3545' },
            { label: 'Brokers', value: brokerSummariesLoading ? '...' : `${filteredBrokers.filter(b => b.connected).length}/${filteredBrokers.length}`, color: filteredBrokers.every(b => b.connected) ? '#28a745' : '#dc3545' },
          ];
          return (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {statCards.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} style={{ flex: index < 4 ? '0 1 110px' : '1 1 140px', minWidth: index < 4 ? '100px' : '130px' }}>
                    <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px', height: '100%' }}>
                      <Card.Body className="p-2">
                        {IconComponent ? (
                          <div className="d-flex align-items-center">
                            <div style={{ background: item.color, borderRadius: '4px', padding: '5px', color: 'white', marginRight: '8px', flexShrink: 0 }}>
                              <IconComponent size={16} />
                            </div>
                            <div>
                              <div style={{ color: '#6c757d', fontSize: '10px', fontWeight: '500' }}>{item.label}</div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: item.color }}>{item.value}</div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ color: '#6c757d', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{item.label}</div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: item.color, marginTop: '2px' }}>{item.value}</div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px', marginBottom: '1rem' }}>
          <Card.Header className="bg-transparent border-0 p-3 pb-0">
            <h5 style={{ margin: '0', fontWeight: '600', fontSize: '14px' }}>Broker-wise P&amp;L</h5>
          </Card.Header>
          <Card.Body className="p-3">
            {brokerSummariesLoading ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : brokerSummaries.length === 0 ? (
              <div style={{ color: '#6c757d', fontSize: '12px' }}>No brokers connected yet — add one in Broker Management.</div>
            ) : (
              <Row className="g-2">
                {brokerSummaries.filter(b => b.broker !== 'ALICE_BLUE').map(b => {
                  const meta = BROKER_META[b.broker] || { avatar: b.broker[0], subtitle: b.broker, color: '#6b7280' };
                  return (
                    <Col xs={12} md={4} lg={3} key={b.broker}>
                      <Card style={{ border: '1px solid #e9ecef', borderRadius: '8px', height: '100%' }}>
                        <Card.Body className="p-2">
                          <div className="d-flex align-items-start justify-content-between mb-2">
                            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                              <div style={{
                                width: '28px', height: '28px', borderRadius: '6px', background: meta.color, color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0,
                              }}>
                                {meta.avatar}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '12px' }}>{BROKER_LABELS[b.broker as BrokerName] || b.broker}</div>
                                <div style={{ fontSize: '9px', color: '#6c757d' }}>{meta.subtitle}</div>
                              </div>
                            </div>
                            {b.connected ? (
                              <Badge bg="success" style={{ fontSize: '9px', fontWeight: 600 }}>● Live</Badge>
                            ) : (
                              <Badge bg="danger" style={{ fontSize: '9px', fontWeight: 600 }}>Error</Badge>
                            )}
                          </div>

                          {!b.connected ? (
                            <>
                              <div style={{
                                background: '#fff5f5', border: '1px solid #ffd7d7', borderRadius: '6px', padding: '8px', marginTop: '8px', marginBottom: '10px',
                              }}>
                                <div className="d-flex align-items-start" style={{ gap: '6px' }}>
                                  <AlertTriangle size={13} color="#dc3545" style={{ marginTop: '1px', flexShrink: 0 }} />
                                  <div style={{ fontSize: '10px', color: '#7f1d1d' }}>{b.error || 'Could not fetch data'}</div>
                                </div>
                              </div>
                              <button
                                onClick={() => navigate('/broker-management')}
                                style={{
                                  width: '100%', padding: '6px 10px', fontSize: '10px', fontWeight: 600, color: 'white',
                                  background: '#1f2937', border: 'none', borderRadius: '6px', cursor: 'pointer',
                                }}
                              >
                                Reconnect in Broker Management
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="d-flex justify-content-between" style={{ fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#6c757d' }}>Realized P&amp;L</span>
                                <span style={{ fontWeight: 600, color: b.realizedPnl === null ? '#9ca3af' : b.realizedPnl >= 0 ? '#28a745' : '#dc3545' }}>
                                  {b.realizedPnl === null ? '--' : formatCurrency(b.realizedPnl)}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between" style={{ fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#6c757d' }}>Unrealized P&amp;L</span>
                                <span style={{ fontWeight: 600, color: b.unrealizedPnl === null ? '#9ca3af' : b.unrealizedPnl >= 0 ? '#28a745' : '#dc3545' }}>
                                  {b.unrealizedPnl === null ? '--' : formatCurrency(b.unrealizedPnl)}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between" style={{ fontSize: '12px', marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid #f1f3f5' }}>
                                <span style={{ fontWeight: 600 }}>Total P&amp;L</span>
                                <span style={{ fontWeight: 700, color: b.totalPnl === null ? '#9ca3af' : b.totalPnl >= 0 ? '#28a745' : '#dc3545' }}>
                                  {b.totalPnl === null ? '--' : formatCurrency(b.totalPnl)}
                                </span>
                              </div>
                              {b.positions.length === 0 ? (
                                <div style={{ color: '#6c757d', fontSize: '10px', marginBottom: '8px' }}>No open positions</div>
                              ) : (
                                <div style={{ maxHeight: '160px', overflowY: 'auto', marginBottom: '8px' }}>
                                  <Table size="sm" className="mb-0" style={{ fontSize: '10px' }}>
                                    <thead>
                                      <tr>
                                        <th>Symbol</th><th>Qty</th><th>Avg</th><th>LTP</th><th>P&amp;L</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {b.positions.map((p, i) => (
                                        <tr key={i}>
                                          <td style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.tradingSymbol}</td>
                                          <td>{p.quantity}</td>
                                          <td>{p.avgPrice.toFixed(2)}</td>
                                          <td>{p.ltp !== null ? p.ltp.toFixed(2) : '--'}</td>
                                          <td style={{ color: (p.pnl ?? 0) >= 0 ? '#28a745' : '#dc3545', fontWeight: 600 }}>
                                            {p.pnl !== null ? p.pnl.toFixed(2) : '--'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              )}
                            </>
                          )}

                          <div className="d-flex justify-content-between" style={{ fontSize: '9px', color: '#9ca3af', paddingTop: '6px', borderTop: '1px solid #f1f3f5' }}>
                            <span>{b.positions.length} Open Position{b.positions.length === 1 ? '' : 's'}</span>
                            <span>Last sync: {lastSyncAt ? lastSyncAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--'}</span>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Daily Reports — NIFTY/SENSEX Buying, client's explicit ask: only yesterday/today */}
        <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px', marginBottom: '16px' }}>
          <Card.Header className="bg-transparent border-0 p-3">
            <h5 style={{ margin: '0', fontWeight: '600', fontSize: '14px' }}>
              <FileText size={16} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
              Daily Reports — NIFTY/SENSEX Buying
            </h5>
          </Card.Header>
          <Card.Body className="pt-0">
            {reports.length === 0 ? (
              <div style={{ color: '#6c757d', fontSize: '12px' }}>Loading...</div>
            ) : (
              <Row className="g-3">
                {reports.map((report) => (
                  <Col xs={12} md={6} key={report.date}>
                    <div style={{
                      border: '1px solid #e9ecef', borderRadius: '6px', padding: '12px 16px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>{report.label}</div>
                        <div style={{ color: '#6c757d', fontSize: '11px' }}>
                          {report.date}
                          {!report.ready && <span style={{ color: '#e08e0b' }}> · available after 3:30 PM</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          size="sm" variant="outline-primary"
                          disabled={!report.ready || reportActionDate === `${report.date}:view`}
                          onClick={() => openReport(report.date, 'view')}
                        >
                          <Eye size={14} style={{ marginRight: '4px' }} />
                          {reportActionDate === `${report.date}:view` ? 'Opening...' : 'View'}
                        </Button>
                        <Button
                          size="sm" variant="outline-secondary"
                          disabled={!report.ready || reportActionDate === `${report.date}:download`}
                          onClick={() => openReport(report.date, 'download')}
                        >
                          <Download size={14} style={{ marginRight: '4px' }} />
                          {reportActionDate === `${report.date}:download` ? 'Preparing...' : 'Download'}
                        </Button>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Recent Trades */}
        <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
          <Card.Header className="bg-transparent border-0 p-3">
            <h5 style={{ margin: '0', fontWeight: '600', fontSize: '14px' }}>Recent Trades</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {stats?.recentTrades && stats.recentTrades.length > 0 ? (
              <Table responsive className="mb-0" style={{ fontSize: '12px' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Time</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Feature</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Symbol</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Type</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Qty</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Price</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Broker</th>
                    <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentTrades.map((trade, i) => (
                    <tr key={i}>
                      <td style={{ padding: '8px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                        {new Date(trade.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '8px' }}>{trade.source}</td>
                      <td style={{ fontWeight: '600', padding: '8px' }}>{trade.trading_symbol}</td>
                      <td style={{ padding: '8px', fontWeight: '600', color: trade.transaction_type === 'BUY' ? '#28a745' : '#dc3545' }}>
                        {trade.transaction_type}
                      </td>
                      <td style={{ padding: '8px' }}>{trade.quantity}</td>
                      <td style={{ padding: '8px' }}>{formatCurrency(trade.price)}</td>
                      <td style={{ padding: '8px' }}>{trade.broker}</td>
                      <td style={{ padding: '8px' }}>
                        <Badge
                          bg={trade.order_status === 'TRADED' || trade.order_status === 'TRANSIT' ? 'success' : trade.order_status === 'REJECTED' ? 'danger' : 'secondary'}
                          style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '4px' }}
                        >
                          {trade.order_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-center py-4">
                <div style={{ color: '#6c757d' }}>
                  <div style={{ background: '#6c757d', borderRadius: '4px', padding: '12px', color: 'white', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <Activity size={24} />
                  </div>
                  <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>No trades found</h6>
                  <small style={{ fontSize: '11px' }}>Start trading to see your positions here</small>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default Dashboard;
